/**
 * 快速创建客户组件 — 在邮件详情页中，当收件人邮箱未匹配到已有客户时，
 * 通过此面板快速录入客户信息并创建记录。
 *
 * 主要导出：`CrmQuickCreateCustomer`（自定义元素 `<crm-quick-create-customer>`）
 *
 * 依赖关系：
 * - 继承 `BaseElement`（统一 Tailwind 样式注入）
 * - 通过 `customerStore` 调用后端客户创建接口
 * - 通过 `COUNTRIES` 常量渲染国家下拉列表
 * - 内部使用 `<crm-select>` 组件渲染可搜索的国家选择器
 *
 * 对外事件：
 * - `crm-customer-created` — 客户创建成功后冒泡，携带新建客户对象
 * - `crm-quick-create-cancel` — 用户点击取消按钮时冒泡，通知父组件关闭面板
 */
import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../base-element.js";
import { customerStore } from "../store.js";
import { toast } from "./toast.js";
import { COUNTRIES } from "../utils/countries.js";
import "./select.js";

@customElement("crm-quick-create-customer")
export class CrmQuickCreateCustomer extends BaseElement {
  /** 预填邮箱地址（从邮件收件人传入），创建客户时一并提交 */
  @property({ type: String }) email = "";

  /** 提交中状态，用于禁用按钮和切换文案 */
  @state() private loading = false;
  /** 表单数据对象，包含客户所有可编辑字段 */
  @state() private formData = {
    name: "",           // 联系人姓名
    company_name: "",   // 公司名称
    job_title: "",      // 职位
    phone: "",          // 联系电话
    address: "",        // 地址
    remark: "",         // 备注
    nation_id: 0,       // 国家 ID（0 表示未选择）
    website: "",        // 公司网站
  };

  /**
   * Lit 生命周期：属性变化后触发。
   * 当 email 属性首次设置或变化时，自动从邮箱地址中截取 @ 前的部分作为默认客户姓名。
   * @param changed - 发生变化的属性 Map
   */
  updated(changed: Map<string, any>) {
    if (changed.has("email") && this.email) {
      const atIndex = this.email.indexOf("@");
      if (atIndex > 0) {
        // 截取 @ 符号之前的字符串作为默认联系人姓名
        this.formData = {
          ...this.formData,
          name: this.email.substring(0, atIndex),
        };
      }
    }
  }

  /**
   * 表单提交处理 — 调用 customerStore 创建客户，成功后派发事件并提示。
   * @param e - 表单 submit 事件
   * @sideeffect 调用后端 API 创建客户，弹出 toast 通知，派发 `crm-customer-created` 事件
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    // 防止重复提交
    if (this.loading) return;

    this.loading = true;
    try {
      // 将表单数据与预填邮箱合并提交
      const customer = await customerStore.createCustomer({
        ...this.formData,
        email: this.email,
      });

      toast.success(`客户 ${customer.name || customer.company_name} 已创建`);

      // 通知父组件客户已创建，携带完整客户对象
      this.dispatchEvent(
        new CustomEvent("crm-customer-created", {
          detail: { customer },
          bubbles: true,
          composed: true,
        })
      );
    } catch (err: any) {
      toast.error(err.message || "创建客户失败");
    } finally {
      this.loading = false;
    }
  }

  /**
   * 取消按钮处理 — 派发 `crm-quick-create-cancel` 事件通知父组件关闭面板。
   */
  private handleCancel() {
    this.dispatchEvent(
      new CustomEvent("crm-quick-create-cancel", {
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * 渲染快速创建客户表单。
   * 布局结构：标题区 → 邮箱（只读）→ 姓名/公司 → 职位/电话 → 国家/网站 → 地址 → 备注 → 操作按钮。
   * @returns Lit 模板
   */
  render() {
    return html`
      <div class="bg-surface border border-border rounded-lg p-4 shadow-lg">
        <div class="mb-3">
          <h3 class="text-base font-semibold text-text">快速创建客户</h3>
          <p class="text-sm text-text-muted mt-1">
            为 <span class="text-text font-medium">${this.email}</span> 创建客户记录
          </p>
        </div>

        <form @submit=${this.handleSubmit} class="space-y-3">
          <div>
            <label class="block text-sm font-medium text-text mb-1">
              邮箱 <span class="text-error">*</span>
            </label>
            <input
              type="email"
              .value=${this.email}
              readonly
              class="w-full px-3 py-1.5 border border-border rounded bg-bg text-text-muted text-sm"
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-text mb-1">姓名</label>
              <input
                type="text"
                .value=${this.formData.name}
                @input=${(e: Event) => {
                  this.formData = {
                    ...this.formData,
                    name: (e.target as HTMLInputElement).value,
                  };
                }}
                class="w-full px-3 py-1.5 border border-border rounded bg-surface text-text text-sm focus:border-primary focus:outline-none"
                placeholder="联系人姓名"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-text mb-1">公司</label>
              <input
                type="text"
                .value=${this.formData.company_name}
                @input=${(e: Event) => {
                  this.formData = {
                    ...this.formData,
                    company_name: (e.target as HTMLInputElement).value,
                  };
                }}
                class="w-full px-3 py-1.5 border border-border rounded bg-surface text-text text-sm focus:border-primary focus:outline-none"
                placeholder="公司名称"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-text mb-1">职位</label>
              <input
                type="text"
                .value=${this.formData.job_title}
                @input=${(e: Event) => {
                  this.formData = {
                    ...this.formData,
                    job_title: (e.target as HTMLInputElement).value,
                  };
                }}
                class="w-full px-3 py-1.5 border border-border rounded bg-surface text-text text-sm focus:border-primary focus:outline-none"
                placeholder="职位"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-text mb-1">电话</label>
              <input
                type="text"
                .value=${this.formData.phone}
                @input=${(e: Event) => {
                  this.formData = {
                    ...this.formData,
                    phone: (e.target as HTMLInputElement).value,
                  };
                }}
                class="w-full px-3 py-1.5 border border-border rounded bg-surface text-text text-sm focus:border-primary focus:outline-none"
                placeholder="联系电话"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-text mb-1">国家</label>
              <crm-select
                fullWidth
                searchable
                .value=${String(this.formData.nation_id)}
                .options=${[
                  { label: "请选择国家", value: "0" },
                  ...COUNTRIES.map((c) => ({
                    label: c.name,
                    value: String(c.id),
                  })),
                ]}
                @change=${(e: CustomEvent) => {
                  this.formData = {
                    ...this.formData,
                    nation_id: parseInt(e.detail.value) || 0,
                  };
                }}
              ></crm-select>
            </div>
            <div>
              <label class="block text-sm font-medium text-text mb-1">网站</label>
              <input
                type="text"
                .value=${this.formData.website}
                @input=${(e: Event) => {
                  this.formData = {
                    ...this.formData,
                    website: (e.target as HTMLInputElement).value,
                  };
                }}
                class="w-full px-3 py-1.5 border border-border rounded bg-surface text-text text-sm focus:border-primary focus:outline-none"
                placeholder="公司网站"
              />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-text mb-1">地址</label>
            <input
              type="text"
              .value=${this.formData.address}
              @input=${(e: Event) => {
                this.formData = {
                  ...this.formData,
                  address: (e.target as HTMLInputElement).value,
                };
              }}
              class="w-full px-3 py-1.5 border border-border rounded bg-surface text-text text-sm focus:border-primary focus:outline-none"
              placeholder="地址"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-text mb-1">备注</label>
            <textarea
              .value=${this.formData.remark}
              @input=${(e: Event) => {
                this.formData = {
                  ...this.formData,
                  remark: (e.target as HTMLTextAreaElement).value,
                };
              }}
              class="w-full px-3 py-1.5 border border-border rounded bg-surface text-text text-sm focus:border-primary focus:outline-none resize-none"
              rows="2"
              placeholder="备注信息"
            ></textarea>
          </div>

          <div class="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              @click=${this.handleCancel}
              class="px-4 py-1.5 text-sm font-medium text-text-secondary border border-border bg-surface hover:bg-bg rounded transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              ?disabled=${this.loading}
              class="px-4 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ${this.loading ? "创建中..." : "创建客户"}
            </button>
          </div>
        </form>
      </div>
    `;
  }
}
